import React, { useState, useEffect } from 'react';
import Header from "@/components/Header";
import Footer from "@/components/FooterSection";
import { useToast } from "@/hooks/use-toast";
import { ReportFormData } from "@/components/ReportForm/formSchema";

// Custom components for this page
import ReportOverview from "@/components/insights/ReportOverview";
import ReportHighlights from "@/components/insights/ReportHighlights";
import CoverSection from "@/components/insights/CoverSection";
import CaseStudiesPreview from "@/components/insights/CaseStudiesPreview";
import KeyFindings from "@/components/insights/KeyFindings";
import CompetitiveAdvantage from "@/components/insights/CompetitiveAdvantage";

const HowSmallLawFirmsAreUsingAi = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const title = "How Small Law Firms Are Using AI to Compete with Larger Practices";
  const pdfPath = "/lovable-uploads/AI-in-Small-and-Mid-Sized-Law-Firms-5-Case-Studies-with-Measurable-Results.pdf";
  
  useEffect(() => {
    document.title = "How Small Law Firms Are Using AI to Compete | DocketLabs";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'How small law firms compete with AI. 5 detailed case studies with measurable results. Download your free report now.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'How small law firms compete with AI. 5 detailed case studies with measurable results. Download your free report now.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);
  
  const handleFormSubmit = async (data: ReportFormData) => {
    setIsLoading(true);
    // New webhook URL
    const webhookUrl = "https://hook.us2.make.com/8qhzemacccuwo3f3f36ss1atdt7he95c";
    
    try {
      await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          ...data,
          report: title,
          timestamp: new Date().toISOString(),
          source: window.location.href
        }),
      });
      
      setIsSuccess(true);
      
      toast({
        title: "Thank you!",
        description: "Your report will be sent to your email shortly.",
      });
      
      setTimeout(() => {
        setIsDialogOpen(false);
        setTimeout(() => {
          setIsSuccess(false);
        }, 300);
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Submission failed",
        description: "There was an issue processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="bg-background text-foreground pt-24 min-h-screen px-6">
        <div className="container mx-auto max-w-5xl py-12">
          {/* Cover section */}
          <CoverSection
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            isSuccess={isSuccess}
            onFormSubmit={handleFormSubmit}
            isLoading={isLoading}
            title={title}
            pdfPath={pdfPath}
          />

          <ReportOverview />
          <ReportHighlights />

          {/* Case Studies Preview */}
          <CaseStudiesPreview />

          {/* Key Findings */}
          <KeyFindings />

          {/* Download report CTA */}
          <CompetitiveAdvantage 
            isDialogOpen={isDialogOpen} 
            setIsDialogOpen={setIsDialogOpen}
            isSuccess={isSuccess}
            onFormSubmit={handleFormSubmit}
            isLoading={isLoading}
            pdfPath={pdfPath}
          />
        </div>
      </main>
      <Footer />
    </>
  );
};

export default HowSmallLawFirmsAreUsingAi;
